import {
  ControlledFrameElementOfKind,
  ControlledFrameKind,
  ControlledFramelikeElement,
  ControlledIFrameElement,
  createControlledFrame,
} from '@fullstory/controlled-iframe';
import { LZJGWDecoder } from '@fullstory/lzjgw';
import type { SelectorChain } from '@fullstory/proto/fs/selectors';
import type { FsId, RecFeature } from '@fullstory/recording-api';
import type {
  DocumentPath,
  FramePath,
  NodePath,
  PlaybackContext,
} from '@fullstory/renderer-shared';
import {
  createCSSRewriter,
  CSSRewriter,
  NodeRewriter,
  NormalizedTagName,
  TagKind,
  toNormalizedTagName,
} from '@fullstory/rewriters';
import { SelectorForElem } from '@fullstory/selectors';
import {
  ASSIGNED_NODE_TAG,
  COMMENT_NODE_TAG,
  DOCUMENT_NODE_TAG,
  POLYFILL_SHADOW_NODE_TAG,
  SHADOW_NODE_TAG,
  TEXT_NODE_TAG,
} from '@fullstory/shared/node_decoder';
import type { OffsetRect, PositionedRect, Size } from '@fullstory/shared/viewport';
import type { Mutable } from '@fullstory/typeutils';
import { UrlRewriter } from '@fullstory/url-rewriter';
import { cacheAndReturn } from '@fullstory/util/cache';
import { thouShallNotPass } from '@fullstory/util/events';
import { Point, topLeft, transformRect } from '@fullstory/util/geometry';
import { wait } from '@fullstory/util/promise';
import type { DocumentEvents } from '../events';
import type { LoadResult } from '../frame_playback_host';
import { FsAnimation } from './fsanimation';
import { FsCanvas } from './fscanvas';
import { FsCSSOMSheet } from './fscssom';
import { FSDialog } from './fsdialog';
import type { FsDocumentNode } from './fsdocumentorshadowroot';
import type { FsDomEventHandlers } from './fsdomeventhandlers';
import type { FsFramelikeElement } from './fsframelikeelement';
import { FsKeyframeEffect } from './fskeyframeeffect';
import { FsElement, FsNode } from './fsnode';
import type { FsNodeMap } from './fsnodemap';
// eslint-disable-next-line import/no-cycle
import { FsNodeConstructorMap } from './fsnodemap';
import { FsNodeTreeBuilder } from './fsnodetreebuilder';
import { PseudoClassManager } from './pseudoclass';
import type { RenderedNode } from './renderednode';
import { ScrollState } from './scrollstate';
import { setValue } from './setvalue';
import { SvgNamespace } from './svg';

// Browsers take time to layout. We need to measure scroll positions to size frames.
// This is a hack to deal with layout settling.
const settleTimeoutMs = 500;

export type FsDocumentInit = {
  ctx: PlaybackContext;
  events: DocumentEvents;
  parent: FsDocument | null;
  handlers: FsDomEventHandlers;
};

export interface FsRootDocument extends FsDocument {
  readonly parent: null;
  readonly pseudoClassManager: PseudoClassManager;
}

export class FsDocument {
  readonly ctx: PlaybackContext;
  readonly events: DocumentEvents;
  readonly path: DocumentPath;
  readonly parent: FsDocument | null;

  readonly baseUrl: string;
  readonly doctype: string;

  /**
   * The iframe that this FsDocument renders into. For the root document, this
   * is the container element passed to the FramePlayback constructor.
   */
  frame: (ControlledFramelikeElement & RenderedNode) | null = null;

  document: HTMLDocument | null = null;

  /** The index of the next RawEvent we should play back for this document. */
  nextEventIndex: number = 0;

  /**
   * The number of resources we've preloaded for this document so far. By
   * comparing this value to the size of the set of preloadable resources at a
   * given point of time in playback, we can efficiently detect when new
   * preloadable resources have become available without scanning the entire
   * list every time.
   */
  resourcesPreloadedSoFar: number = 0;

  readonly byId: FsNode[] = [];
  readonly byDomId: { [id: string]: FsNode } = {};
  deferredIds: FsId[] = [];
  readonly byDetachedCanvasId: FsCanvas[] = [];
  readonly canvasRefs = new Map<number, object>();
  readonly canvasImages = new Map<string, HTMLImageElement>();

  /**
   * Stores rec feature map of what features were turned on at time of recording
   */
  readonly recFeatures = new Map<RecFeature, boolean>();

  /**
   * list of promises that must be resolved prior to flushing
   */
  readonly requiredBeforeFlush: Promise<any>[] = [];

  // Constructed stylesheets, which can be adopted by the document
  // node or shadow roots (CH77956).
  private _constructedStylesheetsById: { [id: number]: FsCSSOMSheet } = {};

  /**
   * NOTE: this array structure preserves references to animations that are no
   * longer used. This will prevent animations from being garbage collected. This
   * behavior is not expected to cause significant problems, but could be investigated
   * as a potential cause if future memory consumption is a problem.
   */
  private _animationsById: FsAnimation[] = [];

  private _dialogsById: { [id: string]: FSDialog } = {};

  /**
   * NOTE: This array structure preserves references to effects that are no longer
   * used. This will cause a known memory leak in the following scenario:
   * 1. KEYFRAME_EFFECT_CREATED (id 1)
   * 2. ANIMATION_CREATED (id 2)
   * 3. KEYFRAME_EFFECT_CREATED (id 3)
   * 4. ANIMATION_PROPERTY_SET (reassigns "effect" to ID 3)
   * The KeyframeEffect with ID 1 is not garbage collected as expected.
   * This shouldn't pose a problem in real-world usage unless a customer site
   * follows this exactly flow a very large number of times, which seems unlikely.
   */
  private _keyframeEffectsById: FsKeyframeEffect[] = [];

  readonly builder: FsNodeTreeBuilder;
  readonly lz: LZJGWDecoder = new LZJGWDecoder();
  idCounter: number = 1;
  styleRuleIdCounter: number = 0;
  ruleOwners = new Map<number, FsCSSOMSheet>();

  selection: {
    startId: number | undefined;
    startOffset: number | undefined;
    endId: number | undefined;
    endOffset: number | undefined;
  } = {
    startId: undefined,
    startOffset: undefined,
    endId: undefined,
    endOffset: undefined,
  };

  // If we resize during VDOM mode, we have to apply the last one at the end.
  pendingResize: (() => void) | null = null;

  /** True if we're inside {@link FsDocument#flush}. */
  inFlush: boolean = false;

  readonly handlers: FsDomEventHandlers;

  private _layoutViewportPosSet = false;
  private _layoutViewportSizeSet = false;
  private _visualViewportSizeSet = false;
  private _layoutViewport: Mutable<PositionedRect> = {
    pageLeft: 0,
    pageTop: 0,
    width: 0,
    height: 0,
  };
  private _visualViewport: Mutable<OffsetRect> = {
    offsetLeft: 0,
    offsetTop: 0,
    width: 0,
    height: 0,
  };
  private _clientArea: Mutable<Size> = { width: 0, height: 0 };

  /** Cached bounding rects for nodes in this document. */
  #boundingRectCache: Map<NodePath, DOMRect> = new Map();
  /** A cached transform from this doc's coordinate system to the root doc. */
  #fromRootDocMatrixCache: DOMMatrixReadOnly | null = null;
  /** A cached transform from the root doc's coordinate system to this doc. */
  #toRootDocMatrixCache: DOMMatrixReadOnly | null = null;
  /** A cached transform from this doc's coordinate system to the renderer. */
  #fromRendererMatrixCache: DOMMatrixReadOnly | null = null;
  /** A cached transform from the renderer to this doc's coordinate system. */
  #toRendererMatrixCache: DOMMatrixReadOnly | null = null;
  /** Cached selectors for nodes in this document. */
  #selectorCache: Map<NodePath, SelectorChain> = new Map();
  /** Cached inner text (Fullstory specific definition, with some whitespace differences) for nodes in this document. */
  #innerTextFsCache: Map<NodePath, string> = new Map();
  /** A cache mapping selectors to the elements they match in this document. */
  #querySelectorAllCache: Map<string, FsElement[]> = new Map();

  /** If true, CSS animations are paused in this document. */
  private _paused: boolean = false;

  documentNode: FsDocumentNode;
  documentElement: FsElement | null = null;
  head: FsElement | null = null;
  body: FsElement | null = null;

  // Document-level scroll state. Shared by both <html> and <body> elements.
  readonly scrollState: ScrollState = new ScrollState();

  #isInteractivelyScrolling: boolean = false;
  #scrollEndTimer: number | undefined = undefined;

  // Custom element definitions that we haven't flushed to the underlying
  // content document yet.
  private _pendingCustomElementDefinitions: string[] = [];

  // Keep track of the states of radio forms
  private readonly _radios: {
    [group: string]: {
      // set of fsids that are in this group
      nodes: Set<number>;

      // fsid of the checked radio
      checked: number | undefined;
    };
  } = {};

  // Implementation for pseudo-classes like :hover.
  // Only set by root docs, since pseudoclass state can be page-global
  // and not document-local.
  readonly pseudoClassManager: PseudoClassManager | null = null;

  // Categories of nodes that we'd like to track.
  readonly byCategory = {
    frame: new Set<FsFramelikeElement>(),
    scrollable: new Set<FsNode>(),
  } as const;

  private readonly _urlRewriter: UrlRewriter;
  readonly inlineCssRewriter: CSSRewriter;
  readonly nodeRewriter: NodeRewriter;

  // The set of all in-flight resources being loaded by this document.
  private _loadingResources = new Set<Promise<void>>();

  constructor(init: FsDocumentInit) {
    this.ctx = init.ctx;
    this.events = init.events;
    this.path = this.events.path;
    this.parent = init.parent;
    this.handlers = init.handlers;
    this.baseUrl = this.events.baseUrl;
    this.doctype = this.events.doctype;
    if (!this.parent) {
      this.pseudoClassManager = new PseudoClassManager(this);
    }
    this.builder = new FsNodeTreeBuilder(this);
    this.documentNode = this.createDocumentNode();
    this._urlRewriter = new UrlRewriter(this.baseUrl);
    this.inlineCssRewriter = createCSSRewriter(this._urlRewriter);
    this.nodeRewriter = new NodeRewriter(
      this.inlineCssRewriter,
      this._urlRewriter,
      this.ctx.flags.features.AppPlaybackSrcsetDescriptorsSupport,
    );
  }

  isRoot(): this is FsRootDocument {
    return this.parent === null;
  }

  /** Create a frame suitable for holding this FsDocument. */
  createFrame<Kind extends ControlledFrameKind>(
    inDocument: Document,
    frameKind: Kind,
  ): ControlledFrameElementOfKind[Kind] {
    return createControlledFrame(this.ctx, inDocument, this.doctype, frameKind, this.baseUrl);
  }

  #handlePointerEvent = (event: PointerEvent) => {
    this.handlers.onPointerEvent(this, event);
  };

  #handleAndBlockPointerEvent = (event: PointerEvent) => {
    this.#handlePointerEvent(event);
    thouShallNotPass(event);
  };

  #handleScrollEvent = (event: Event) => {
    const eventType = event.type;
    this.#handleScrollEventType(eventType);
    if (eventType === 'scroll') {
      // scrollend isn't fired reliably in all cases
      // if we haven't seen a scroll event in 250ms, let's manually fire a scrollend
      window.clearTimeout(this.#scrollEndTimer);
      this.#scrollEndTimer = window.setTimeout(() => {
        this.#handleScrollEventType('scrollend');
      }, 250);
    }
  };

  #handleScrollEventType = (eventType: string) => {
    let type: 'scrollstart' | 'scroll' | 'scrollend' = 'scroll';
    if (eventType === 'scroll' && !this.#isInteractivelyScrolling) {
      type = 'scrollstart';
      this.#isInteractivelyScrolling = true;
    } else if (eventType === 'scrollend') {
      type = 'scrollend';
      this.#isInteractivelyScrolling = false;
    }
    this.handlers.onScrollEvent(this, type);
  };

  /**
   * Attach this FsDocument to an frame, giving it a real DOM to render its
   * vDOM into. The frame should normally be obtained by calling
   * {@link FsDocument#createFrame()}.
   */
  async attachToFrame(frame: ControlledFramelikeElement): Promise<void> {
    if (frame === this.frame) {
      return;
    }

    if (this.isAttached()) {
      throw new Error(`Reattaching attached FsDocument`);
    }

    await frame.loaded;

    if (!frame.contentDocument || !frame.contentWindow) {
      throw new Error(`Failed to load iframe while attaching FsDocument`);
    }

    // TODO: Assert that the iframe's doctype matches the doctype we requested.
    // This is currently tricky because EdgeHTML normalizes doctypes differently
    // than other browsers; we should revisit this once we drop support for
    // EdgeHTML.

    this.frame = frame;
    this.document = frame.contentDocument as HTMLDocument;
    this.documentNode.invalidateDomSubtree();
    this.documentNode.dom = this.document;

    // Remove all nodes in the document, so we start with a clean slate.
    while (this.document.lastChild) {
      this.document.removeChild(this.document.lastChild);
    }

    // Block all native clicks.
    const wnd = frame.contentWindow;
    wnd.removeEventListener('submit', thouShallNotPass, true);
    wnd.removeEventListener('mousedown', thouShallNotPass, true);
    wnd.removeEventListener('click', thouShallNotPass, true);
    wnd.addEventListener('submit', thouShallNotPass, true);
    wnd.addEventListener('mousedown', thouShallNotPass, true);
    wnd.addEventListener('click', thouShallNotPass, true);

    wnd.removeEventListener('pointerdown', this.#handleAndBlockPointerEvent, true);
    wnd.removeEventListener('pointerup', this.#handleAndBlockPointerEvent, true);
    wnd.removeEventListener('pointermove', this.#handlePointerEvent, true);
    wnd.addEventListener('pointerdown', this.#handleAndBlockPointerEvent, true);
    wnd.addEventListener('pointerup', this.#handleAndBlockPointerEvent, true);
    wnd.addEventListener('pointermove', this.#handlePointerEvent, {
      capture: true,
      passive: true,
    });

    wnd.removeEventListener('scroll', this.#handleScrollEvent, true);
    wnd.removeEventListener('scrollend', this.#handleScrollEvent, true);
    wnd.addEventListener('scroll', this.#handleScrollEvent, {
      capture: true,
      passive: true,
    });
    wnd.addEventListener('scrollend', this.#handleScrollEvent, {
      capture: true,
      passive: true,
    });
  }

  detach(): void {
    this.frame = null;
    this.document = null;
    this.forEachChildDoc(childDoc => childDoc.detach());
  }

  frameOffset(): { left: number; top: number } {
    if (!this.parent || !this.frame) {
      return { left: 0, top: 0 };
    }
    const { left, top } = this.frame.getBoundingClientRect();
    return { left, top };
  }

  isAttached(): boolean {
    if (!this.frame) {
      return false;
    }
    const fsFrameEl = this.frame._fs as FsFramelikeElement | undefined;
    if (!fsFrameEl) {
      return true;
    } // Top-level frame
    if (fsFrameEl.getContentDocument() !== this) {
      return false;
    }
    return fsFrameEl.isAttached();
  }

  /**
   * Create a new FsAnimation
   *
   * TODO(jordanhurt) - Add support for timelineId argument
   *
   * NOTE: During seeking, this function may be called with previously used
   * animation IDs as old events are replayed. In this scenario, this function
   * will recreate a new FsAnimation with the same ID and overwrite the old one
   * in order to ensure the KeyframeEffect and DOM target are up to date.
   */
  createAnimation(id: number, effectId?: number | null) {
    if (this.animationById(id)) {
      // NOTE: this scenario _should_ occur during seeking within a single page
      console.log(`Animation ID "${id}" is already allocated and will be overwritten`);
    }

    const effect = this.keyframeEffectById(effectId);
    const animation = new FsAnimation(effect);

    // Set the ID to make debugging easier
    animation.id = `${id}`;

    this._animationsById[id] = animation;

    return animation;
  }

  createDialog(id: number) {
    const dialog = new FSDialog();
    this._dialogsById[id] = dialog;
    return dialog;
  }

  dialogsById(id: number): FSDialog | undefined {
    return this._dialogsById[id];
  }

  animationById(id: number | undefined): FsAnimation | undefined {
    if (typeof id === 'number') {
      return this._animationsById[id];
    }
    return undefined;
  }

  /**
   * Creates a new FsKeyframeEffect
   *
   * NOTE: During seeking, this function may be called with previously used
   * IDs as old events are replayed. In that scenario, this function will
   * create a new FsKeyframeEffect with the same ID and overwrite old one
   * in order to ensure the "target" property has an up-to-date DOM node reference.
   */
  createKeyframeEffect(
    id: number,
    targetId: FsId | null | undefined,
    keyframes: Keyframe[] | null | undefined,
    options?: KeyframeEffectOptions,
  ) {
    if (this.keyframeEffectById(id)) {
      // NOTE: this scenario _should_ occur during seeking within a single page
      console.log(`KeyframeEffect ID "${id}" is already allocated and will be overwritten`);
    }

    const target = typeof targetId === 'number' ? this.byId[targetId] : null;
    const effect = new FsKeyframeEffect(target ?? null, keyframes ?? null, options);

    this._keyframeEffectsById[id] = effect;

    return effect;
  }

  keyframeEffectById(id: number | null | undefined): FsKeyframeEffect | undefined {
    if (typeof id === 'number') {
      return this._keyframeEffectsById[id];
    }
    return undefined;
  }

  styleSheetById(sheetId: number): FsCSSOMSheet {
    const sheet = this._constructedStylesheetsById[sheetId];
    if (sheet) {
      return sheet;
    }
    return (this._constructedStylesheetsById[sheetId] = new FsCSSOMSheet(this, []));
  }

  removeNode(id: number) {
    const toRemove = this.byId[id];
    if (!toRemove) {
      console.log(`MUT_REMOVE: Unexpectedly missing target node ${id}`);
      return;
    }

    if (!toRemove.parent) {
      console.log(`spurious remove: ${toRemove.tag} : ${id}`);
      return;
    }

    toRemove.parent.removeChild(toRemove);
    this.markNodeDetached(toRemove);
  }

  private markNodeDetached(node: FsNode) {
    node.setDetached();

    for (let child = node.firstChild; child; child = child.next) {
      this.markNodeDetached(child);
    }
  }

  get frameZoom(): number {
    if (!this._visualViewportSizeSet || !this._layoutViewportSizeSet) {
      return 1;
    }
    if (this._visualViewport.width == 0) {
      return 1;
    }

    // TODO(sethf): For now we assume that the visual viewport has the same
    // aspect ratio as the layout viewport, so we don't actually make use of
    // the height when computing the zoom. Chrome plans to start shrinking the
    // visual viewport for the on-screen keyboard and other UI, so we'll
    // eventually have to handle that case.
    return this._layoutViewport.width / this._visualViewport.width;
  }

  get visualViewport(): OffsetRect {
    return this._visualViewport;
  }
  get layoutViewport(): PositionedRect {
    return this._layoutViewport;
  }
  get clientArea(): Size {
    return this._clientArea;
  }

  // Returns true if we know the size and position of the layout viewport and
  // the size of the visual viewport. (We use the default value for the visual
  // viewport offset if one isn't explicitly set, so we don't need to wait.)
  isViewportSet(): boolean {
    return this._layoutViewportPosSet && this._visualViewportSizeSet && this._layoutViewportSizeSet;
  }

  setVisualViewportOffset(
    visualViewportOffsetLeft: number,
    visualViewportOffsetTop: number,
    when: number,
  ) {
    if (when < this.scrollState.watermark) {
      return;
    }
    this._visualViewport.offsetLeft = visualViewportOffsetLeft;
    this._visualViewport.offsetTop = visualViewportOffsetTop;
  }

  setLayoutViewportPos(layoutViewportLeft: number, layoutViewportTop: number, when: number) {
    if (when < this.scrollState.watermark) {
      return;
    }
    this._layoutViewportPosSet = true;
    this._layoutViewport.pageLeft = layoutViewportLeft;
    this._layoutViewport.pageTop = layoutViewportTop;
  }

  setVisualViewportSize(visualViewportWidth: number, visualViewportHeight: number) {
    this._visualViewportSizeSet = true;
    this._visualViewport.width = visualViewportWidth;
    this._visualViewport.height = visualViewportHeight;
  }

  setLayoutViewportSize(layoutViewportWidth: number, layoutViewportHeight: number) {
    this._layoutViewportSizeSet = true;
    this._layoutViewport.width = layoutViewportWidth;
    this._layoutViewport.height = layoutViewportHeight;

    if (!this._visualViewportSizeSet) {
      this.setVisualViewportSize(layoutViewportWidth, layoutViewportHeight);
    }
  }

  setClientArea(clientWidth: number, clientHeight: number) {
    this._clientArea.width = clientWidth;
    this._clientArea.height = clientHeight;
  }

  /** @returns the transform from this doc's coordinate system to the root doc. */
  get toRootDocMatrix(): DOMMatrixReadOnly {
    if (this.#toRootDocMatrixCache) {
      return this.#toRootDocMatrixCache;
    }

    // If we're the top-level document, the transform is just the identity
    // matrix. The same applies if we're detached.
    if (!this.parent || !this.frame) {
      return (this.#toRootDocMatrixCache = new DOMMatrix());
    }

    // If this frame has no translation or scale relative to the parent, we can
    // just use the parent's transform as-is.
    const offset = this.frameOffset();
    const zoom = this.frameZoom;
    if (offset.left === 0 && offset.top === 0 && zoom === 1) {
      return (this.#toRootDocMatrixCache = this.parent.toRootDocMatrix);
    }

    // Combine this frame's local translation and scale with the parent's
    // transform to obtain the cumulative transform for this frame.
    const transform = DOMMatrix.fromMatrix(this.parent.toRootDocMatrix);
    transform.translateSelf(offset.left, offset.top);
    transform.scaleSelf(zoom);
    return (this.#toRootDocMatrixCache = transform);
  }

  /** @returns the transform from the root doc's coordinate system to this doc. */
  get fromRootDocMatrix(): DOMMatrixReadOnly {
    if (this.#fromRootDocMatrixCache) {
      return this.#fromRootDocMatrixCache;
    }
    return (this.#fromRootDocMatrixCache = this.toRootDocMatrix.inverse());
  }

  /** @returns the transform from this doc's coordinate system to the renderer. */
  get toRendererMatrix(): DOMMatrixReadOnly {
    if (this.#toRendererMatrixCache) {
      return this.#toRendererMatrixCache;
    }

    // If we're not the root doc, just let the root doc compute the transform to
    // the renderer and combine it with our transform to the root doc.
    const rootDoc = this.getRootDoc();
    if (rootDoc !== this) {
      return (this.#toRendererMatrixCache = rootDoc.toRendererMatrix.multiply(
        this.toRootDocMatrix,
      ));
    }

    // If the root doc is detached or has not been flushed to the DOM, the
    // transform is just the identity matrix.
    const rootDocFrameBounds = rootDoc.frame?.getBoundingClientRect();
    if (!rootDocFrameBounds) {
      return (this.#toRendererMatrixCache = new DOMMatrix());
    }
    const rootDocViewportWidth = rootDoc.documentElement?.dom?.clientWidth ?? 0;
    if (rootDocViewportWidth <= 0) {
      return (this.#toRendererMatrixCache = new DOMMatrix());
    }

    // (0, 0) in the root doc's coordinate system maps to the top-left corner of
    // the root doc's frame.
    const translation = topLeft(rootDocFrameBounds);

    // The scale factor is the ratio of the width of the root doc's frame to the
    // width of the root doc's viewport.
    const scale = rootDocFrameBounds.width / rootDocViewportWidth;

    const toRendererMatrix = new DOMMatrix();
    toRendererMatrix.translateSelf(translation.x, translation.y);
    toRendererMatrix.scaleSelf(scale);
    return (this.#toRendererMatrixCache = toRendererMatrix);
  }

  /** @returns the transform from the renderer's coordinate system to this doc. */
  get fromRendererMatrix(): DOMMatrixReadOnly {
    if (this.#fromRendererMatrixCache) {
      return this.#fromRendererMatrixCache;
    }
    return (this.#fromRendererMatrixCache = this.toRendererMatrix.inverse());
  }

  /** @returns the bounding rect of the given node in the renderer's coordinate system. */
  getRendererBoundingRectFor(node: FsNode): DOMRect {
    if (!node.path) {
      return new DOMRect();
    }

    const cachedRect = this.#boundingRectCache.get(node.path);
    if (cachedRect) {
      return cachedRect;
    }

    if (!node.dom || !node.isElement()) {
      return new DOMRect();
    }

    const rect = transformRect(this.toRendererMatrix, node.dom.getBoundingClientRect());
    return cacheAndReturn(this.#boundingRectCache, node.path, rect);
  }

  /** Invalidates caches related to node positions and sizes. */
  invalidateGeometryCaches(): void {
    this.#boundingRectCache.clear();
    this.#toRootDocMatrixCache = null;
    this.#fromRootDocMatrixCache = null;
    this.#toRendererMatrixCache = null;
    this.#fromRendererMatrixCache = null;
  }

  /**
   * Invalidates caches related to node positions and sizes, in this document
   * and all of its descendants.
   */
  invalidateGeometryCachesForAllDescendants(): void {
    for (const doc of this.documentTree()) {
      doc.invalidateGeometryCaches();
    }
  }

  /** @returns a CSS selector for the given node, in structured form. */
  getSelectorFor(node: FsNode): SelectorChain | undefined {
    if (!node.path) {
      return undefined;
    }

    const cachedSelector = this.#selectorCache.get(node.path);
    if (cachedSelector) {
      return cachedSelector;
    }

    if (!node.dom || !node.isElement()) {
      return undefined;
    }

    const selector = SelectorForElem(node.dom as HTMLElement);
    if (!selector) {
      return undefined;
    }

    return cacheAndReturn(this.#selectorCache, node.path, selector);
  }

  /** @returns inner text (Fullstory specific definition, with some whitespace differences) for the given node. */
  getInnerTextFsFor(node: FsNode): string | undefined {
    if (!node.path) {
      return undefined;
    }

    const cachedText = this.#innerTextFsCache.get(node.path);
    if (cachedText !== undefined) {
      return cachedText;
    }

    return cacheAndReturn(this.#innerTextFsCache, node.path, this.#innerTextFs(node));
  }

  #innerTextFs(node: FsNode): string {
    const nodeTexts: string[] = [];
    this.#innerTextFsHelper(node, nodeTexts);
    return nodeTexts.join(' ');
  }

  #innerTextFsHelper(node: FsNode, nodeTexts: string[]) {
    if (node.isText()) {
      const textContent = node.getTextContent();
      if (textContent) {
        nodeTexts.push(textContent);
      }
    }
    for (const child of node.childNodes) {
      this.#innerTextFsHelper(child, nodeTexts);
    }
  }

  /** Invalidates caches related to document content. */
  invalidateContentCaches(): void {
    this.#selectorCache.clear();
    this.#querySelectorAllCache.clear();
    this.#innerTextFsCache.clear();
  }

  /**
   * @returns elements in this document or any of its descendants that intersect
   * the given point in the renderer's coordinate system.
   */
  elementsFromRendererPoint(rendererPoint: Point): FsElement[] {
    if (!this.document) {
      return [];
    }

    const docPoint = this.fromRendererMatrix.transformPoint(rendererPoint);
    try {
      const results: FsElement[] = [];
      for (const element of this.document.elementsFromPoint(docPoint.x, docPoint.y)) {
        const node = (element as RenderedNode)._fs;
        if (!node || !node.path || !node.isElement()) {
          continue;
        }
        if (node.isFrame()) {
          const frameDoc = node.getContentDocument();
          const frameElements = frameDoc?.elementsFromRendererPoint(rendererPoint) ?? [];
          results.push(...frameElements);
        }
        results.push(node);
      }
      return results;
    } catch (e) {
      console.log('elementsFromRendererPoint for failed:', e);
      return [];
    }
  }

  /** @returns elements in this document that match the given selector. */
  querySelectorAll(selector: string): FsElement[] {
    if (!this.document) {
      return [];
    }

    const cachedResults = this.#querySelectorAllCache.get(selector);
    if (cachedResults) {
      return cachedResults;
    }

    try {
      const results: FsElement[] = [];
      for (const element of [...this.document.querySelectorAll(selector)]) {
        const node = (element as RenderedNode)._fs;
        if (!node || !node.path || !node.isElement()) {
          continue;
        }
        results.push(node);
      }
      return cacheAndReturn(this.#querySelectorAllCache, selector, results);
    } catch (e) {
      console.log('querySelectorAll for', selector, 'failed:', e);
      return cacheAndReturn(this.#querySelectorAllCache, selector, []);
    }
  }

  /**
   * @returns elements in this document or any of its descendants that match the
   * given selector.
   */
  querySelectorInDescendants(selector: string): FsElement[] {
    const results: FsElement[] = [];
    for (const doc of this.documentTree()) {
      results.push(...doc.querySelectorAll(selector));
    }
    return results;
  }

  // Try to synchronize the scroll positions of scrollable elements in the DOM
  // with the recorded scroll positions in the vDOM. See this PR for more
  // details about why we need to do this:
  //   https://github.com/cowpaths/mn/pull/8201
  syncScrollPositions() {
    for (const node of this.byCategory['scrollable']) {
      node.flushScrollPosition();
    }

    this.forEachChildDoc(childDoc => childDoc.syncScrollPositions());
  }

  /**
   * Sets all scrollable items to the top left on their scrollable area.
   */
  zeroScrollPositions() {
    for (const node of this.byCategory['scrollable']) {
      node.zeroScrollPositions();
    }

    this.forEachChildDoc(childDoc => childDoc.zeroScrollPositions());
  }

  // Update the given node's membership in the categories we track. Called
  // after a relevant change to the given node.
  updateNodeCategories(node: FsNode) {
    // Helper nodes are implementation details and, in a sense, aren't truly
    // part of the document, so they aren't include in any categories.
    if (node.isHelper()) {
      return;
    }

    if (node.isAttached() && node.isFrame()) {
      this.byCategory['frame'].add(node);
    } else {
      this.byCategory['frame'].delete(node as any);
    }

    if (node.isAttached() && node.knownScrollable) {
      this.byCategory['scrollable'].add(node);
    } else {
      this.byCategory['scrollable'].delete(node);
    }

    if (node.isAttached()) {
      if (node.tag == 'html') {
        this.documentElement = node as FsElement;
      } else if (node.tag == 'head') {
        this.head = node as FsElement;
      } else if (node.tag == 'body') {
        this.body = node as FsElement;
      }
    } else if (node == this.documentElement) {
      this.documentElement = null;
    } else if (node == this.head) {
      this.head = null;
    } else if (node == this.body) {
      this.body = null;
    }
  }

  createDocumentNode(): FsDocumentNode {
    const node = this.createNode(DOCUMENT_NODE_TAG);
    node.setAttached();
    return node;
  }

  createNode<TagName extends keyof FsNodeMap>(tag: TagName): FsNodeMap[TagName] {
    // Unlike other nodes, the document node is preconstructed when an
    // FsDocument is created, so we handle it specially here. Note that we do
    // need to check that documentNode has been initialized, because this
    // function is called in the FsDocument constructor to create that
    // preconstructed document node in the first place.
    // TODO(sethf): We should stop handling the document node specially.
    if (tag === DOCUMENT_NODE_TAG && this.documentNode) {
      return this.documentNode;
    }

    const id = this.idCounter++;
    const node = createFsNode(this, tag, id);
    this.byId[id] = node;
    if (node.isDeferred()) {
      this.deferredIds.push(id);
    }
    return node;
  }

  createHelperNode<TagName extends keyof FsNodeMap>(tag: TagName): FsNodeMap[TagName] {
    return createFsNode(this, tag);
  }

  // Catch errors in flush, since throwing would derail playback.
  async flush(): Promise<void> {
    try {
      this.inFlush = true;
      await this._flushImpl();
    } catch (e) {
      console.log(`Error flushing document: ${e}`);
    } finally {
      this.inFlush = false;
    }
  }

  private async _flushImpl(): Promise<void> {
    if (!this.isAttached()) {
      return;
    }

    try {
      if (this.requiredBeforeFlush.length > 0) {
        await Promise.all(this.requiredBeforeFlush);
      }
    } catch (e) {
      console.warn(`Error waiting for requiredBeforeFlush`, e);
    }

    if (this.requiredBeforeFlush.length > 0) {
      this.requiredBeforeFlush.splice(0, this.requiredBeforeFlush.length);
    }

    // detached nodes need to be flushed before document gets flushed because they may be used by document nodes
    for (const id in this.byDetachedCanvasId) {
      this.byDetachedCanvasId[id]?.ensureNode();
      this.byDetachedCanvasId[id]?.flush();
    }

    this.documentNode.flush();

    // Attach contained documents.
    for (const frame of this.byCategory['frame']) {
      const childDoc = frame.getContentDocument();
      if (childDoc) {
        await childDoc.attachToFrame(frame.dom as ControlledIFrameElement);
      }
    }

    for (const id in this._animationsById) {
      this._animationsById[id].flush();
    }

    for (const id of Object.keys(this._dialogsById)) {
      const dialogEl = this.byId[parseInt(id, 10)]?.dom;
      this._dialogsById[id].flush(dialogEl as HTMLDialogElement | undefined);
    }

    // Define custom elements.
    if (this._pendingCustomElementDefinitions.length > 0) {
      for (const definedTagName of this._pendingCustomElementDefinitions) {
        this._flushCustomElementDefinition(definedTagName);
      }
      this._pendingCustomElementDefinitions = [];
    }

    if (this.pendingResize) {
      const r = this.pendingResize;
      r();
      setTimeout(() => {
        if (!this.pendingResize) {
          r();
        }
      }, settleTimeoutMs);
      this.pendingResize = null;
    }

    // Set the values of radio forms
    for (const k of Object.keys(this._radios)) {
      const group = this._radios[k];
      for (const fsid of group.nodes) {
        const node = this.byId[fsid];
        if (!node?.dom) {
          group.nodes.delete(fsid);
          continue;
        }
        setValue(node.dom as HTMLInputElement, (group.checked === fsid).toString());
      }

      if (group.nodes.size === 0) {
        delete this._radios[k];
      }
    }

    this.renderSelection();

    // Conservatively assume that anything may have changed.
    this.invalidateContentCaches();
    this.invalidateGeometryCaches();
  }

  /** Register value changes for radio button nodes
   * NOTE: if value is undefined, the node will be removed from the group
   * @param overrideName if specified, will use this string instead of the node's attr('name')
   */
  setRadioValue(node: FsNode, value: string | undefined, overrideName?: string) {
    const fsid = node.fsid;
    if (!fsid) {
      return;
    }
    const groupName = this.getRadioButtonGroup(node, overrideName);
    if (groupName == null) {
      return;
    }
    this._populateRadioGroup(groupName);
    const group = this._radios[groupName];
    if (typeof value === 'undefined') {
      group.nodes.delete(fsid);
      if (group.nodes.size === 0) {
        delete this._radios[groupName];
        return;
      }
    } else {
      group.nodes.add(fsid);
    }
    if (value === 'true') {
      group.checked = fsid;
    } else if (group.checked === fsid) {
      group.checked = undefined;
    }
  }

  private _populateRadioGroup(group: string) {
    if (this._radios[group]) {
      return;
    }

    this._radios[group] = {
      nodes: new Set<number>(),
      checked: undefined,
    };
  }

  *children(): IterableIterator<FsDocument> {
    for (const frame of this.byCategory['frame']) {
      const childDoc = frame.getContentDocument();
      if (childDoc) {
        yield childDoc;
      }
    }
  }

  /**
   * Yields this document and all its descendants in depth-first traversal order.
   */
  *documentTree(): IterableIterator<FsDocument> {
    yield this;
    for (const childDoc of this.children()) {
      yield* childDoc.documentTree();
    }
  }

  forEachChildDoc(action: (childDoc: FsDocument) => void): void {
    for (const doc of this.children()) {
      action(doc);
    }
  }

  get paused(): boolean {
    return this._paused;
  }

  getRootDoc(): FsRootDocument {
    let doc: FsDocument = this;
    while (doc.parent) {
      doc = doc.parent;
    }
    return doc as FsRootDocument;
  }

  /**
   * Note that the document for a given input can change over time, as
   * iframes reload.
   */
  getRelatedFrameDoc(path: FramePath): FsDocument | undefined {
    // Walk up to root, then down through descendants.
    let doc: FsDocument | undefined = this.getRootDoc();
    for (const frameId of path.components) {
      const frame = doc.byId[frameId] as Partial<FsFramelikeElement>;
      doc = frame?.getContentDocument?.();
      if (!doc) {
        return undefined;
      }
    }
    return doc;
  }

  getRelatedNode({ framePath, nodeId }: NodePath): FsNode | undefined {
    return this.getRelatedFrameDoc?.(framePath)?.byId[nodeId];
  }

  /**
   * Pause or resume playback of CSS animations in this document and its
   * descendant documents.
   */
  setPaused(paused: boolean) {
    if (this._paused === paused) {
      return;
    }
    if (!this.document) {
      return;
    }
    if (typeof this.document.getAnimations !== 'function') {
      return;
    }

    // Pause CSS Animations, CSS Transitions, and Web Animations
    for (const animation of this.document.getAnimations()) {
      const isCSSAnimation = 'animationName' in animation;
      const isCSSTransition = 'transitionProperty' in animation;
      const isWebAnimation = !isCSSAnimation && !isCSSTransition;

      // NOTE: Web Animations have complex timelines, so we can't
      // assume they should always resume playing like we can
      // with CSS Animations and CSS Transitions
      if (paused && animation.playState === 'running') {
        animation.pause();
      } else if (!paused && !isWebAnimation && animation.playState === 'paused') {
        animation.play();
      }
    }

    this.forEachChildDoc(childDoc => {
      childDoc.setPaused(paused);
    });

    this._paused = paused;
  }

  // Returns a string of the owner "OWNER_FSID:RADIO_NAME" corresponding to the radio
  // button group of node. In the case of radio buttons hanging out inside a shadow
  // dom sans a form, we can consider the shadowRoot as being the namespace boundary
  // for all radio buttons.
  private getRadioButtonGroup(node: FsNode, overrideName?: string): string | null {
    if (node.attr('type') != 'radio') {
      return null;
    }

    const name = overrideName || node.attr('name') || `_fs_radio_${node.fsid}`;

    let buttonOwner;
    if (node.attr('form')) {
      buttonOwner = this.byDomId[node.attr('form')];
    } else {
      const seen = new WeakMap<FsNode, boolean>();
      buttonOwner = node;
      while (buttonOwner && buttonOwner.tag != 'form') {
        if (seen.has(buttonOwner) || buttonOwner.shadow) {
          break;
        }
        seen.set(buttonOwner, true);
        buttonOwner = buttonOwner.parent;
      }
    }

    let ownerId;
    if (buttonOwner?.tag === 'form') {
      ownerId = buttonOwner.fsid;
    } else if (buttonOwner?.shadow) {
      ownerId = buttonOwner.shadow.fsid;
    }
    ownerId = ownerId ?? 0;
    const group = `${ownerId}:${name}`;
    return group;
  }

  defineCustomElement(tagName: string): void {
    if (!tagName.includes('-')) {
      console.warn(`Rejecting invalid custom element tag '${tagName}'`);
      return;
    }

    this._pendingCustomElementDefinitions.push(tagName);
  }

  private _flushCustomElementDefinition(tagName: string): void {
    if (!this.frame) {
      return;
    }

    const wnd = this.frame.contentWindow as Window & {
      HTMLElement: typeof HTMLElement;
    };
    if (!wnd) {
      throw new Error(`Defining a custom element before a document is attached`);
    }

    if (!wnd.customElements.get(tagName)) {
      // define custom element so that any element with this tag would be defined
      // we don't store the actual implementation, because we track the inner DOM
      // so defining it to a dummy here just to trigger :defined pseudo class
      wnd.customElements.define(
        tagName,
        class extends wnd.HTMLElement {
          constructor() {
            super();
          }
        },
      );
    }
  }

  setSelection(startId?: number, startOffset?: number, endId?: number, endOffset?: number) {
    this.selection = { startId, startOffset, endId, endOffset };
  }

  private renderSelection(): void {
    if (!this.frame) {
      return;
    }

    const wnd = this.frame.contentWindow;
    if (!wnd) {
      throw new Error(`Rendering selection without a DOM`);
    }

    const selection = wnd.getSelection();
    const clearSelection = () => selection && selection.removeAllRanges();

    const { startId, startOffset, endId, endOffset } = this.selection;
    if (startId === undefined || endId === undefined) {
      clearSelection();
      return;
    }

    const start = this.byId[startId];
    if (!start) {
      console.log(`Missing selection anchor node${startId}`);
      clearSelection();
      return;
    }

    const end = this.byId[endId];
    if (!end) {
      console.log(`Missing selection focus node${endId}`);
      clearSelection();
      return;
    }

    if (!selection) {
      // getSelection() can return null (CH48896).
      console.log('Missing selection for window.');
      return;
    }

    try {
      selection.setBaseAndExtent(start.dom!, startOffset || 0, end.dom!, endOffset || 0);
    } catch (e) {
      console.debug(`Error setting window selection: ${e}`);
      throw e;
    }
  }

  // Add a resource to the set of in-flight resources being loaded by this
  // document. Returns a callback which can be used to signal that the resource
  // has finished loading. After `timeoutMs` milliseconds, the resource will be
  // treated as if it has finished loading regardless of whether the callback
  // was called.
  addLoadingResource(
    relHref: string,
    destination: RequestDestination,
    timeoutMs: number = 5000,
  ): (result: LoadResult) => void {
    let href = relHref.trim();
    let generateResourceEvents = true;

    if (href !== '') {
      try {
        href = new URL(relHref, this.baseUrl).href;
      } catch (_) {
        console.warn(`Couldn't convert to absolute URL: ${relHref}`);
      }
    } else {
      // Leave empty URLs as-is, and don't generate resource events for them.
      // They're guaranteed to fail to load, so knowing that they failed doesn't
      // tell us anything useful about e.g. whether we should display the local
      // resources warning. They're also often generated by the playback code
      // itself in cases where we want to block or defer loading of a resource, so
      // they're effectively an implementation detail.
      console.debug(`Loading resource with empty URL for destination '${destination}'`);
      generateResourceEvents = false;
    }

    if (generateResourceEvents) {
      this.handlers.onResourceLoadStart(href, destination);
    }

    let resolve: (result: LoadResult) => void;
    const resourceLoaded = new Promise<LoadResult>(r => (resolve = r));

    // initial value to make eslint happy
    let loadingPromise: Promise<void> = Promise.resolve();
    const waitForResource = (async () => {
      const result = await resourceLoaded;
      this._loadingResources.delete(loadingPromise);
      if (generateResourceEvents) {
        this.handlers.onResourceLoaded(href, destination, result);
      }
    })();

    loadingPromise = Promise.race([wait(timeoutMs), waitForResource]);
    this._loadingResources.add(loadingPromise);
    return resolve!;
  }

  // Returns a string key for a FontFace object that can be used to check if two
  // FontFace objects are equivalent.
  fontFaceToKey(fontFace: FontFace): string {
    return [
      fontFace.ascentOverride,
      fontFace.descentOverride,
      fontFace.display,
      fontFace.family,
      fontFace.featureSettings,
      fontFace.lineGapOverride,
      fontFace.status,
      fontFace.stretch,
      fontFace.unicodeRange,
      fontFace.weight,
      fontFace.style,
    ].join(':');
  }

  // Returns a promise that resolves when all in-flight loading resources in
  // this document or any of its child documents have finished loading; this can
  // be used to reduce the likelihood of FOUC. The promise does not wait for any
  // resources that start loading after this function has been called. To
  // prevent long delays, the promise always resolves after a short timeout.
  loadingResources(maxWaitTime = 2000): Promise<void> {
    if (this._loadingResources.size > 0) {
      console.debug(`Awaiting ${this._loadingResources.size} loading resources...`);
    }
    const resourcesInFlight: Promise<void>[] = [...this._loadingResources];

    this.forEachChildDoc(childDoc => {
      resourcesInFlight.push(childDoc.loadingResources(maxWaitTime));
    });

    const allLoaded = (async () => {
      try {
        // Wait for the currently loading set of resources.
        await Promise.all(resourcesInFlight);

        // Wait for this document's fonts to load. Note that to catch as many
        // fonts as possible, we need to wait for the other resources to load,
        // because stylesheets may add additional fonts to the document's
        // FontFaceSet.
        //
        // It's also worth noting that this can cause fonts to load that were
        // referenced in stylesheets but not actually referenced in the document.
        // There's unfortunately no way to avoid that with the API they give us,
        // because the FontFaceSet#ready promise that you're supposed to use for
        // this resolves instantly for dynamically constructed documents that are
        // initially empty, and that's exactly the case we're dealing with in
        // playback.
        const fontPromises: Promise<FontFace>[] = [];
        const seenFonts = new Set<string>();
        this.document?.fonts.forEach(fontFace => {
          const fontkey = this.fontFaceToKey(fontFace);
          if (seenFonts.has(fontkey)) {
            return;
          }
          fontPromises.push(fontFace.load());
          seenFonts.add(fontkey);
        });
        await Promise.all(fontPromises);
      } catch (e) {
        console.warn(`Error while waiting for loading resources: ${e}`);
      }
    })();

    const timeout = wait(maxWaitTime);
    return Promise.race([allLoaded, timeout]);
  }

  maybeCreateDetachedCanvasById(id: number): FsCanvas {
    const canvas = this.byDetachedCanvasId[id];
    if (canvas) {
      return canvas;
    }

    return (this.byDetachedCanvasId[id] = new FsCanvas(this));
  }
}

/**
 * Create a new FsNode.
 * @param ownerDoc - The document that owns the new node.
 * @param rawTagName - The node's tag name. This will be automatically
 * lowerspaced and canonicalized. (Partly for security reasons, so that we're
 * sure that e.g. a <script> element's tag name will always be 'script'.)
 * @param fsid - The new node's FsId, or undefined if it's a helper node
 * that doesn't come from the recording.
 */
function createFsNode<TagName extends keyof FsNodeMap>(
  ownerDoc: FsDocument,
  rawTagName: TagName,
  fsid?: FsId | undefined,
): FsNodeMap[TagName] {
  let namespace: string | undefined;
  let namespacelessRawTagName: string = rawTagName.toString();

  if (namespacelessRawTagName.startsWith('$')) {
    // Short circuit with an existing node. This is used for `FsAssignedNodes`.
    const id = parseInt(namespacelessRawTagName.substring(1), 10);
    const target = ownerDoc.byId[id];
    if (!target) {
      console.warn(
        `stream contains a backreference ${namespacelessRawTagName} but target was not found`,
      );
    } else {
      return target;
    }
  }

  if (namespacelessRawTagName.startsWith('svg:')) {
    namespacelessRawTagName = namespacelessRawTagName.slice(4);
    namespace = SvgNamespace;
  }

  const tagName = cleanTagName(
    namespacelessRawTagName,
    namespace === SvgNamespace ? 'svg' : 'html',
  );

  const constructorName = tagName as keyof typeof FsNodeConstructorMap;
  const constructor = FsNodeConstructorMap[constructorName];
  if (constructor) {
    return new constructor(ownerDoc, fsid);
  }
  return new FsNode(ownerDoc, tagName, namespace, fsid);
}

function isElementTag(tagName: string): boolean {
  return (
    tagName !== '!doctype' &&
    tagName !== COMMENT_NODE_TAG &&
    tagName !== DOCUMENT_NODE_TAG &&
    tagName !== POLYFILL_SHADOW_NODE_TAG &&
    tagName !== SHADOW_NODE_TAG &&
    tagName !== TEXT_NODE_TAG &&
    tagName !== ASSIGNED_NODE_TAG
  );
}

/** Characters that may appear in a valid HTML tag name. */
const ValidTagNameRe = /^[a-z0-9._:-]+$/;

/** SVG tags are case-sensitive */
const ValidSvgTagNameRe = /^[a-zA-Z0-9._:-]+$/;

/** ASCII characters which aren't control characters like NUL or ESC.
 * Additionally, tolerate 0xa0 (nbsp) because we see them often enough in customer's website and it's tolerated by chrome */
const AsciiNonControlCharsRe = /^[ -~\t\n\r\xa0]+$/;

function cleanTagName(rawTagName: string, kind: TagKind): NormalizedTagName {
  const tagName = toNormalizedTagName(rawTagName, kind);
  const isValidTagName =
    kind === 'svg' ? ValidSvgTagNameRe.test(tagName) : ValidTagNameRe.test(tagName);

  if (isElementTag(tagName) && !isValidTagName) {
    // The HTML spec requires that tag names include only alphanumeric ASCII
    // characters. (Both uppercase and lowercase are allowed, but we don't need to
    // check for uppercase since we call toLowerCase() above.) Browsers are not that
    // strict in practice, so we allow some selected punctuation characters as well.
    // But if we find something else, that's suspicious.

    const reportInvalidTag = (severity: 'debug' | 'error'): void => {
      console[severity](`Detected an invalid HTML tag "<${tagName}>" in the recorded DOM.`);
    };

    // Customers do hit this sometimes, so be flexible and replace with a
    // <div> if the tag name contains only normal ASCII characters.
    // IE in particular reports the empty string for HTML5 and custom tag names.
    if (AsciiNonControlCharsRe.test(tagName) || tagName == '') {
      reportInvalidTag('debug');
      return toNormalizedTagName('div');
    }

    reportInvalidTag('error');
    return toNormalizedTagName('noscript');
  }

  if (tagName === 'script') {
    // This either indicates a bug or, more sinisterly, indicates that
    // someone malicously injected a <script> element into the DOM by
    // talking to /rec/bundle directly. (The console error doesn't include
    // the latter case to minimize the chance for customer panic.)
    console.error(
      'Detected a <script> element in the recorded DOM. This ' +
        'indicates a bug in the recording script.',
    );
    return toNormalizedTagName('noscript');
  }

  return tagName;
}
